/**
 * ============================================================
 * BORN FRAGRANCE — WISHLIST ENGINE
 * bf-wishlist.js
 * ============================================================
 * Native localStorage wishlist — no external app required.
 *
 * Features:
 * - Add / remove products
 * - Duplicate prevention
 * - Persistent across sessions (localStorage)
 * - Live badge updates via custom events
 * - Guest + logged-in compatible
 * - Renders wishlist grid on /pages/wishlist
 * - Add-to-cart from wishlist page
 *
 * Storage key: 'bf-wishlist'
 * Schema: Array of { id, handle, title, price, image, url, vendor }
 *
 * Public API (window.BFWishlist):
 *   .add(product)    — add product object
 *   .remove(id)      — remove by variant/product id
 *   .has(id)         — boolean check
 *   .getAll()        — returns array
 *   .count()         — returns integer
 *   .toggle(product) — add if absent, remove if present
 * ============================================================ */

(function () {
  'use strict';

  var STORAGE_KEY = 'bf-wishlist';

  /* ── Storage helpers ── */
  function readWishlist() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) { return []; }
  }

  function writeWishlist(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      localStorage.setItem('bf-wishlist-count', String(items.length));
    } catch (e) { /* quota exceeded */ }
  }

  function emit(count) {
    document.dispatchEvent(new CustomEvent('bf:wishlist:updated', {
      bubbles: true,
      detail: { count: count }
    }));
  }

  /* ── Public API ── */
  var BFWishlist = {
    getAll: function () { return readWishlist(); },
    count: function () { return readWishlist().length; },
    has: function (id) {
      return readWishlist().some(function (item) {
        return String(item.id) === String(id);
      });
    },
    add: function (product) {
      var items = readWishlist();
      var exists = items.some(function (i) { return String(i.id) === String(product.id); });
      if (!exists) {
        items.push(product);
        writeWishlist(items);
        emit(items.length);
      }
      return !exists;
    },
    remove: function (id) {
      var items = readWishlist().filter(function (i) {
        return String(i.id) !== String(id);
      });
      writeWishlist(items);
      emit(items.length);
    },
    toggle: function (product) {
      if (BFWishlist.has(product.id)) {
        BFWishlist.remove(product.id);
        return false;
      } else {
        BFWishlist.add(product);
        return true;
      }
    },
    clear: function () {
      writeWishlist([]);
      emit(0);
    }
  };

  window.BFWishlist = BFWishlist;

  /* ══════════════════════════════════════════════════════════
     WISHLIST PAGE RENDERER
     Runs only on /pages/wishlist
     ══════════════════════════════════════════════════════════ */
  function isWishlistPage() {
    return window.location.pathname === '/pages/wishlist';
  }

  if (!isWishlistPage()) return;

  var container = document.getElementById('bf-wishlist-page');
  if (!container) return;

  /* ── Format price (Shopify returns cents) ── */
  function formatPrice(cents) {
    var amount = (parseInt(cents, 10) / 100).toFixed(2);
    return 'AED ' + amount;
  }

  /* ── Render the wishlist grid ── */
  function renderWishlist() {
    var items = BFWishlist.getAll();
    container.innerHTML = '';

    if (items.length === 0) {
      container.innerHTML = [
        '<div class="bf-wishlist__empty">',
        '  <div class="bf-wishlist__empty-icon" aria-hidden="true">',
        '    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">',
        '      <path d="M42 18c0-6.075-4.925-11-11-11a10.97 10.97 0 0 0-7 2.528A10.97 10.97 0 0 0 17 7C10.925 7 6 11.925 6 18c0 2.9 1.112 5.54 2.93 7.51L24 41.5l15.07-15.99A10.96 10.96 0 0 0 42 18z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
        '    </svg>',
        '  </div>',
        '  <h2 class="bf-wishlist__empty-title">Your wishlist is empty</h2>',
        '  <p class="bf-wishlist__empty-text">Save the fragrances you love and find them here whenever you\'re ready.</p>',
        '  <a href="/collections/all" class="bf-wishlist__cta">Explore Fragrances</a>',
        '</div>'
      ].join('');
      return;
    }

    var grid = document.createElement('div');
    grid.className = 'bf-wishlist__grid';

    items.forEach(function (item) {
      var card = document.createElement('div');
      card.className = 'bf-wishlist__card';
      card.dataset.id = item.id;

      card.innerHTML = [
        '<div class="bf-wishlist__card-media">',
        '  <a href="' + item.url + '" aria-label="View ' + escHtml(item.title) + '">',
        '    <img src="' + escHtml(item.image) + '" alt="' + escHtml(item.title) + '" loading="lazy" width="400" height="400">',
        '  </a>',
        '  <button class="bf-wishlist__remove" data-id="' + item.id + '" aria-label="Remove ' + escHtml(item.title) + ' from wishlist" type="button">',
        '    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round"/></svg>',
        '  </button>',
        '</div>',
        '<div class="bf-wishlist__card-info">',
        '  ' + (item.vendor ? '<p class="bf-wishlist__vendor">' + escHtml(item.vendor) + '</p>' : ''),
        '  <h3 class="bf-wishlist__title"><a href="' + item.url + '">' + escHtml(item.title) + '</a></h3>',
        '  <p class="bf-wishlist__price">' + formatPrice(item.price) + '</p>',
        '  ' + (item.available !== false
            ? '<button class="bf-wishlist__atc button" data-variant-id="' + item.variantId + '" type="button">Add to Cart</button>'
            : '<p class="bf-wishlist__sold-out">Sold Out</p>'),
        '</div>'
      ].join('');

      grid.appendChild(card);
    });

    container.appendChild(grid);

    /* Add continue shopping link */
    var footer = document.createElement('div');
    footer.className = 'bf-wishlist__footer';
    footer.innerHTML = '<a href="/collections/all" class="bf-wishlist__cta bf-wishlist__cta--outline">Continue Shopping</a>';
    container.appendChild(footer);

    /* Bind remove buttons */
    container.querySelectorAll('.bf-wishlist__remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        BFWishlist.remove(btn.dataset.id);
        renderWishlist();
      });
    });

    /* Bind add-to-cart buttons */
    container.querySelectorAll('.bf-wishlist__atc').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = btn.dataset.variantId;
        if (!variantId) return;
        btn.textContent = 'Adding\u2026';
        btn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        })
        .then(function (r) { return r.json(); })
        .then(function () {
          btn.textContent = 'Added ✓';
          // Trigger cart refresh
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
          setTimeout(function () {
            btn.textContent = 'Add to Cart';
            btn.disabled = false;
          }, 1800);
        })
        .catch(function () {
          btn.textContent = 'Error — Retry';
          btn.disabled = false;
        });
      });
    });
  }

  function escHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* Initial render */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderWishlist, { once: true });
  } else {
    renderWishlist();
  }

  /* Re-render on wishlist change */
  document.addEventListener('bf:wishlist:updated', renderWishlist);

})();
